import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { rpc } from "../api";
import { createHistoryPager, emptyHistory } from "../moderationHistory";

export function ModerationHistory() {
  const [state, setState] = useState(emptyHistory);
  const pager = useRef<ReturnType<typeof createHistoryPager> | null>(null);
  useEffect(() => {
    const current = createHistoryPager(
      (cursor) =>
        rpc("get_moderation_history", {
          p_before: cursor?.created_at ?? null,
          p_before_id: cursor?.id ?? null,
          p_limit: 50,
        }),
      setState,
    );
    pager.current = current;
    void current.refresh();
    return () => {
      current.dispose();
      pager.current = null;
    };
  }, []);
  return (
    <section aria-label="Moderation history" aria-busy={state.loading}>
      <div className="admin-section-heading">
        <h2>Review history</h2>
        <p>
          Newest decisions first. This restricted log shows actions and record
          IDs; submitted content and private review notes stay out of this view.
          Deleted records appear without an ID.
        </p>
      </div>
      <button
        type="button"
        className="button secondary small"
        disabled={state.loading}
        onClick={() => void pager.current?.refresh()}
      >
        <RefreshCw size={15} /> Refresh history
      </button>
      {state.error && (
        <p role="alert" className="form-error">
          {state.error}
        </p>
      )}
      <p role="status" className="small muted">
        {state.loading
          ? "Loading review history…"
          : state.entries.length
            ? `${state.entries.length} decisions loaded.`
            : state.error
              ? "No history is displayed."
              : "No review decisions yet."}
      </p>
      <ol className="admin-history-list" aria-label="Review decisions">
        {state.entries.map((entry) => (
          <li key={entry.id} className="review-card admin-card">
            <div className="admin-card-main">
              <h3>
                {entry.kind.replaceAll("_", " ")} ·{" "}
                {entry.action.replaceAll("_", " ")}
              </h3>
              <p>
                <time dateTime={entry.created_at}>
                  {new Date(entry.created_at).toLocaleString()}
                </time>
              </p>
              <p className="admin-id">
                Record: {entry.target_id ?? "Deleted record"}
              </p>
              <p className="admin-id">
                Moderator: {entry.actor_id ?? "Deleted moderator"}
              </p>
              <small className="admin-id">Decision {entry.id}</small>
            </div>
          </li>
        ))}
      </ol>
      {state.hasMore && (
        <button
          type="button"
          className="button secondary"
          disabled={state.loading}
          onClick={() => void pager.current?.more()}
        >
          Load older decisions
        </button>
      )}
    </section>
  );
}
